
<template>
    <div
        v-if="isOpen"
        class="automation-element-list"
        @keyup.esc="close"
    >
        <div class="sidebar-header">
            <template>
                <DsIconButton name="x" @click="close" />
                <span class="sidebar-header-title">
                    {{ $t('funnel.findElement.title') }}
                </span>
            </template>
            <div class="spacer" />
            <FilterActionButton
                v-model="selectedFilterKeys"
                :cells="cells"
                :published-cells="publishedCells"
                @chip:add="upsertChip"
            />
        </div>

        <div class="sidebar-toolbar">
            <DsInlineAlert v-if="!hasElements" type="info" class="no-elements">
                {{ $t('funnel.element.list.modal.noElements') }}
            </DsInlineAlert>

            <template v-else>
                <DsInputField
                    v-model="searchString"
                    type="search"
                    class="search-input"
                    data-qa="search-input"
                    :placeholder="$t('funnel.campaign.search.placeholder')"
                >
                    <template #leading>
                        <DsIcon name="search" />
                    </template>
                </DsInputField>

                <div v-if="chips.length" class="chips-row">
                    <DsChip
                        v-for="chip in chips"
                        :key="chip.key"
                        remove
                        trailing-icon="x"
                        with-interaction
                        @mousedown.native.stop.prevent="removeChip(chip)"
                    >
                        <template #leading>
                            <DsIcon name="filter" />
                        </template>
                        <template #default>
                            {{ chip.label }}
                        </template>
                    </DsChip>
                </div>

                <div v-if="searchString && !hasSearchResults" class="search-not-found">
                    {{ $t('funnel.element.list.modal.filteredElements.search', { searchString }) }}
                </div>
            </template>
        </div>


        <div class="content-scroll">
            <template v-if="hasElements && (!searchString || hasSearchResults)">
                <DsCollapsible
                    v-for="section in visibleSections"
                    :key="section.id"
                    :is-open="searchString ? true : expandedSections.includes(section.id)"
                    @toggle="searchString ? null : handleToggle(section.id)"
                >
                    <template #header>
                        <div class="collapsible-header">
                            <div class="header-left">
                                <div
                                    class="element-section-title"
                                    :data-qa="`section-title-${section.id}`"
                                >
                                    {{ $t(section.label) }}
                                </div>
                                <div
                                    class="element-section-subtitle"
                                    :data-qa="`section-count-${section.id}`"
                                >
                                    {{ $tc('funnel.findElement.count', section.elements.length, { count: section.elements.length }) }}
                                </div>
                            </div>
                            <div v-if="!searchString" class="chevron-wrapper">
                                <DsIcon :name="expandedSections.includes(section.id) ? 'chevron-down' : 'chevron-right'" />
                            </div>
                        </div>
                    </template>

                    <template #default>
                        <DisplayList
                            :elements="section.elements"
                            :query="searchString"
                            @click="onClickElement"
                        />
                    </template>
                </DsCollapsible>
            </template>
        </div>

        <div class="bottom-bar">
            <DsOutlineButton @click="close">
                {{ $t('global.close') }}
            </DsOutlineButton>
        </div>
    </div>
</template>

<script>
import { getIconPathByCellType } from '../../utils/cell/automation-display-utils';
import {
    CELL_TOOLTIP,
    DECISION_TYPE,
    EDGE_TYPE,
    FLOW_TYPE,
    GOALS,
    INTEGRATION_ACTION_TYPE,
    INTEGRATION_TRIGGER_TYPE,
    MAIN_CANVAS_ID,
    NOTES_TYPE,
    SEQUENCE_START,
} from '../../utils/editor.constants';

import DisplayList from './DisplayList.vue';
import FilterActionButton from './FilterActionButton.vue';

const SECTIONS = {
    NONE: 'none',
    WHEN: 'when',
    THEN: 'then',
    DECISION: 'decision',
    NOTES: 'notes',
    STATUS: 'status',
};

const STATUS_SECTIONS = {
    NONE: 'status_none',
    ERROR: 'error',
    SETUP_REQUIRED: 'setupRequired',
    PUBLISHED: 'published',
    UNPUBLISHED: 'unpublished',
};

export default {
    components: {
        DisplayList,
        FilterActionButton,
    },
    props: {
        isOpen: Boolean,
        cells: {
            type: Array,
            default: () => ([]),
        },
        publishedCells: {
            type: Array,
            default: () => ([]),
        },
        enabledIntegrations: {
            type: Array,
            default: () => ([]),
        },
    },
    SECTIONS,
    STATUS_SECTIONS,
    emits: ['close-element-list', 'set-sequence-id', 'open-config-modal', 'open-note', 'highlight-cell', 'request-focus-cell'],
    data() {
        return {
            searchString: '',
            sectionSelected: { id: SECTIONS.NONE },
            statusSectionSelected: { id: STATUS_SECTIONS.NONE },
            expandedSections: [],
            selectedFilterKeys: [],
            chips: [],
        };
    },
    computed: {
        parsedFilters() {
            const out = {
                whenTypes: new Set(),
                thenTypes: new Set(),
                status: new Set(),
                sectionOnly: new Set(),
            };

            for (const k of this.selectedFilterKeys) {
                const [fam, rest] = String(k).split(':');

                if (fam === 'when') out.whenTypes.add(rest);
                else if (fam === 'then') out.thenTypes.add(rest);
                else if (fam === 'status') out.status.add(rest);
                else if (fam === 'section') out.sectionOnly.add(rest);
            }

            return out;
        },

        activeSectionFilters() {
            const s = new Set();

            if (this.parsedFilters.whenTypes.size) s.add(SECTIONS.WHEN);
            if (this.parsedFilters.thenTypes.size) s.add(SECTIONS.THEN);
            for (const sec of this.parsedFilters.sectionOnly) s.add(sec);

            return s;
        },

        visibleSections() {
            const ids = Object.values(SECTIONS)
                .filter((id) => id !== SECTIONS.NONE && id !== SECTIONS.STATUS);

            const q = (this.searchString || '').toLowerCase();

            return ids
                .map((id) => {
                    let elements = this.elementsBySection(id);

                    elements = this.applyFiltersToSection(id, elements);
                    if (q) elements = elements.filter((e) => e.searchInfo.toLowerCase().includes(q));

                    return {
                        id,
                        label: this.$t(`funnel.findElement.${id}`),
                        elements,
                    };
                })
                .filter((sec) => sec.elements.length > 0);
        },
        hasSearchResults() {
            return this.visibleSections.some((s) => s.elements.length);
        },

        hasElements() { return this.allElements.length > 0; },

        sections() {
            const ids = Object.values(SECTIONS)
                .filter((id) => id !== SECTIONS.NONE && id !== SECTIONS.STATUS);

            return ids.map((id) => {
                const elements = this.elementsBySection(id);

                return {
                    id,
                    label: this.$t(`funnel.findElement.${id}`),
                    elements,
                };
            }).filter((section) => {
                return section.elements.length > 0;
            });
        },

        allElements() {
            return this.cells.map((cell) => {
                const displayLabel = this.getCellTypeLabel(cell);
                const status = this.getStatus(cell);
                const statusKey = this.toStatusKey(status);
                let name = cell.value?.name;
                let searchInfo = [cell.value?.name, displayLabel, this.$t(status)].join(' ');

                if (cell.type === NOTES_TYPE) {
                    name = cell.value?.notes.replace(/<[^>]*>/g, '');
                    searchInfo = [cell.value?.name, displayLabel, name].join(' ');
                }

                const isWhen = this.isWhen(cell.type);
                const isThen = !isWhen && cell.type !== DECISION_TYPE && cell.type !== NOTES_TYPE && cell.type !== SEQUENCE_START;
                const effectiveColor = cell.type === DECISION_TYPE ? 'jump-to-element' : 'neutral';

                return {
                    cell,
                    searchInfo,
                    name,
                    displayLabel,
                    status: this.$t(status),
                    statusKey,
                    isWhen,
                    isThen,
                    isBroken: this.isBroken(cell),
                    iconSrc: getIconPathByCellType(cell.type, effectiveColor),
                    setupRequired: status === CELL_TOOLTIP.elementSetupRequired,
                    isPublished: status === CELL_TOOLTIP.elementPublished,
                    isUnpublishedChanges: status === CELL_TOOLTIP.elementPublishedWithChanges,
                };
            }).filter(({ cell }) => {
                if ([EDGE_TYPE].includes(cell.type)) return false;
                if (parseInt(cell.meta.id, 10) < 2) return false;

                return true;
            });
        },
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                this.expandedSections = [];
                this.searchString = '';
            }
        },
    },
    methods: {
        toStatusKey(i18nKey) {
            const {
                elementSetupRequired,
                elementReadyShort,
                elementPublished,
                elementPublishedWithChanges,
                elementBroken,
            } = CELL_TOOLTIP;

            if (i18nKey === elementSetupRequired) return 'setupRequired';
            if (i18nKey === elementReadyShort) return 'ready';
            if (i18nKey === elementPublished) return 'published';
            if (i18nKey === elementPublishedWithChanges) return 'unpublished';
            if (i18nKey === elementBroken) return 'error';

            return '';
        },

        applyFiltersToSection(sectionId, list) {
            const pf = this.parsedFilters;

            if (this.activeSectionFilters.size && !this.activeSectionFilters.has(sectionId)) {
                return [];
            }

            let out = list;

            if (sectionId === SECTIONS.WHEN && pf.whenTypes.size) {
                out = out.filter((e) => pf.whenTypes.has(e.cell.type));
            }

            if (sectionId === SECTIONS.THEN && pf.thenTypes.size) {
                out = out.filter((e) => pf.thenTypes.has(e.cell.type));
            }

            if (pf.status.size) {
                out = out.filter((e) => pf.status.has(e.statusKey));
            }

            return out;
        },

        elementsBySection(sectionId) {
            switch (sectionId) {
            case SECTIONS.WHEN:
                return this.allElements.filter(({ isWhen }) => isWhen);
            case SECTIONS.THEN:
                return this.allElements.filter(({ isThen }) => isThen);
            case SECTIONS.NOTES:
                return this.allElements.filter(({ cell }) => cell.type === NOTES_TYPE);
            case SECTIONS.DECISION:
                return this.allElements.filter(({ cell }) => cell.type === DECISION_TYPE);
            case SECTIONS.STATUS:
                return this.allElements.filter(({ isBroken, setupRequired, isPublished, isUnpublishedChanges }) => isBroken || setupRequired || isPublished || isUnpublishedChanges);
            default:
                return [];
            }
        },

        upsertChip(meta) {
            const i = this.chips.findIndex((c) => c.key === meta.key);

            if (i >= 0) this.chips.splice(i, 1, meta);
            else this.chips.push(meta);
        },

        removeChip({ key }) {
            this.chips = this.chips.filter((c) => c.key !== key);
            this.selectedFilterKeys = this.selectedFilterKeys.filter((k) => k !== key);
        },

        handleToggle(sectionId) {
            this.toggleSection(sectionId);
        },

        toggleSection(sectionId) {
            if (this.expandedSections.includes(sectionId)) {
                this.expandedSections = this.expandedSections.filter((id) => id !== sectionId);
            } else {
                this.expandedSections = [...this.expandedSections, sectionId];
            }
        },

        close() {
            this.sectionSelected = { id: SECTIONS.NONE };
            this.statusSectionSelected = { id: STATUS_SECTIONS.NONE };
            this.$emit('close-element-list');
        },

        getIconPathByCellType,

        getCellTypeLabel(cardCell) {
            switch (cardCell.type) {
            case INTEGRATION_TRIGGER_TYPE:
                return this.enabledIntegrations.find((integration) => {
                    return cardCell.value.integrationTriggerId === integration.id;
                })?.name;
            case INTEGRATION_ACTION_TYPE:
                return this.enabledIntegrations.find((integration) => {
                    return cardCell.value.integrationActionId === integration.id;
                })?.name;
            case FLOW_TYPE:
                return this.$t('funnel.edit.sideBar.template.flow');
            default:
                return this.getTypeLabel(cardCell.type);
            }
        },

        getTypeLabel(type) {
            const isWhen = this.$te(`funnel.sidebar.when.cell.${type}.label`);
            const isThen = this.$te(`funnel.sidebar.then.cell.${type}.label`);
            const isOther = this.$te(`funnel.type.${type}.label`);
            const cellTypeLabel = isWhen ? `funnel.sidebar.when.cell.${type}.label`
                : isThen ? `funnel.sidebar.then.cell.${type}.label`
                    : isOther ? `funnel.type.${type}.label` : type;

            return this.$t(cellTypeLabel);
        },

        onClickElement(cell) {
            const isSequence = cell.type === FLOW_TYPE;
            const parentId = cell?.meta?.parent;

            if (isSequence) {
                this.$emit('request-focus-cell', cell);
                this.$emit('highlight-cell', cell);

                return;
            }

            if (parentId && parentId !== MAIN_CANVAS_ID) {
                this.$emit('set-sequence-id', parentId);
                this.$nextTick(() => {
                    this.$emit('request-focus-cell', cell);
                    this.$emit('highlight-cell', cell);
                });

                return;
            }

            this.$emit('request-focus-cell', cell);
            this.$emit('highlight-cell', cell);
        },

        getStatus(cell) {
            if (cell.type === NOTES_TYPE) {
                return '';
            }

            if (this.isBroken(cell)) {
                return CELL_TOOLTIP.elementBroken;
            }

            if (!this.isInitialized(cell)) {
                return CELL_TOOLTIP.elementSetupRequired;
            }

            if (this.publishedCells.length) {
                if (this.isPublished(cell)) {
                    return CELL_TOOLTIP.elementPublished;
                }

                if (this.cellHasBeenPublished(cell)) {
                    return CELL_TOOLTIP.elementPublishedWithChanges;
                }
            }

            if (this.isReady(cell)) {
                return CELL_TOOLTIP.elementReadyShort;
            }

            return CELL_TOOLTIP.elementSetupRequired;
        },

        cellHasBeenPublished(cell) {
            return !!this.publishedCells.find((publishedCell) => publishedCell.id === cell.id);
        },

        isInitialized(cell) {
            return cell?.value?.initialized === '1';
        },

        isBroken(cell) {
            return cell?.value?.broken === '1';
        },

        isReady(cell) {
            return cell?.value?.ready === '1';
        },

        isPublished(cell) {
            return cell?.value?.published === '1';
        },

        isWhen(type) {
            return [...Object.values(GOALS), INTEGRATION_TRIGGER_TYPE].includes(type);
        },
    },
};
</script>

<style lang="scss" scoped>
$shortcut-sidebar-width: 360px;

.automation-element-list {
    --collapsible-inner-padding: 0 0 $spacing-300 0;
    font-size: $font-size-xs;
    width: $shortcut-sidebar-width;
    height: 100vh;
    border: 1px solid $color-gray-200;
    background-color: $color-paper;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 50;

    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.sidebar-header {
    position: sticky;
    top: 0;
    z-index: 3;
    background: $color-paper;
    border-bottom: 1px solid $color-gray-300;

    display: flex;
    align-items: center;
    padding: $spacing-100;
    width: $shortcut-sidebar-width;

    .sidebar-header-title {
        font-size: $font-size-lg;
    }

    .spacer { flex: 1; }
}

.sidebar-toolbar {
    position: sticky;
    top: calc(#{$spacing-100} + #{$font-size-lg} + 2px);
    top: $spacing-600;
    z-index: 2;
    background: $color-paper;
    padding-bottom: $spacing-050;

    .search-input {
        margin: $spacing-200;
        width: calc($shortcut-sidebar-width - $spacing-400);
    }

    .no-elements {
        margin: $spacing-200;
    }

    .chips-row {
        width: calc($shortcut-sidebar-width - $spacing-400);
        margin: $spacing-075 $spacing-200 $spacing-075;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: $spacing-100;
        white-space: normal;
    }

    .search-not-found {
        margin: $spacing-200;
        color: $color-text-subtle;
    }
}

.content-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    padding-top: $spacing-100;
}

.bottom-bar {
    position: sticky;
    bottom: 0;
    margin-top: auto;
    padding: $spacing-100;
    background-color: $color-white;
    display: flex;
    justify-content: flex-start;
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
    z-index: 1;
}

.collapsible-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: $spacing-150;
    cursor: pointer;
    width: 100%;
    min-height: $spacing-700;
}

.header-left {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    text-align: left;
    flex-grow: 1;
    margin-left: $spacing-075;

    .element-section-title {
        font-size: $font-size-md;;
        font-weight: 500;
        line-height: $spacing-300;
    }

    .element-section-subtitle {
        font-size: $font-size-sm;
        font-weight: 400;
        color: $color-text-subtle;
        line-height: $spacing-200;
        margin-top: $spacing-025;
    }
}

.chevron-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-left: $spacing-150;

    ::v-deep(svg) {
        width: $spacing-200;
        height: $spacing-200;
    }
}

.search-input {
    margin: $spacing-200;
    width: calc($shortcut-sidebar-width - $spacing-400);
}

.search-not-found {
    margin: $spacing-200;
    color: $color-text-subtle;
}

.no-elements {
    margin: $spacing-200;
}

.search-result-wrapper {
    margin: $spacing-200;
}

.chips-row {
    width: calc($shortcut-sidebar-width - $spacing-400);
    margin: $spacing-075 $spacing-200 $spacing-075;

    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $spacing-100;
    white-space: normal;
}
</style>
